import {
  CardPreview,
  Dialog,
  type DialogProps,
  LabeledDivider,
} from "@verde/compose-ui";
import {
  fundOperationQueryKeys,
  useCreateFundAnalysesUpdateRequest,
  useFetchFundAnalysesUpdateRequest,
} from "@verde/entities";
import {
  CheckCircleSolidIcon,
  ChevronRightIcon,
  CircleStackIcon,
  EditIcon,
  ExternalLinkIcon,
  QuestionMarkIcon,
  RefreshIcon,
  UsersIcon,
} from "@verde/icons";
import {
  Alert,
  Label,
  Link,
  Skeleton,
  Switch,
  Tag,
  TagFilter,
  Tooltip,
} from "@verde/ui-core";
import { queryClient } from "@verde/utils";
import CreditDialog from "modules/credit-profile/components/credit-dialog/credit-dialog";
import React, { useCallback, useMemo, useReducer, useState } from "react";

type RefreshDialogProps = Pick<DialogProps, "onClose">;

const MONITORING_GROUPS = [
  {
    value: "all",
    label: "Todas as operações em monitoramento",
  },
  {
    value: "over120",
    label: "Superior a 120 dias",
  },
  {
    value: "between91_120",
    label: "Entre 91 a 120 dias",
  },
  {
    value: "between61_90",
    label: "Entre 61 a 90 dias",
  },
  {
    value: "between31_60",
    label: "Entre 31 a 60 dias",
  },
  {
    value: "between0_30",
    label: "Entre 0 a 30 dias",
  },
] as const;

function MonitoringRefreshDialog({ onClose }: RefreshDialogProps) {
  const [buyOpen, toggleBuy] = useReducer((state) => !state, false);
  const [selectedGroups, setSelectedGroups] = useState<Array<string>>([]);
  const [tab, setTab] = useState<"default" | "consume">("default");

  const create = useCreateFundAnalysesUpdateRequest();
  const updateRequest = useFetchFundAnalysesUpdateRequest({
    groups: selectedGroups,
  });

  const loading = updateRequest.isLoading && tab === "consume";

  const hasBalanceToBuy = useMemo(() => {
    if (!updateRequest.data) return false;

    return (
      updateRequest.data.total_update_cost <= updateRequest.data.current_balance
    );
  }, [updateRequest.data]);

  const fullUpdated = useMemo(() => {
    return !updateRequest.data?.update_clients_count;
  }, [updateRequest.data]);

  const onSubmit = useCallback(async () => {
    if (!hasBalanceToBuy) {
      toggleBuy();
      return;
    }

    await create.mutateAsync({
      groups: selectedGroups,
    });

    queryClient.invalidateQueries(fundOperationQueryKeys.list({}));

    onClose?.();
  }, [hasBalanceToBuy, create, selectedGroups, onClose]);

  return (
    <>
      <Dialog
        open
        size="sm"
        onClose={onClose}
        header={{
          size: "lg",
          icon: <RefreshIcon />,
          title: "Atualização Raio-X de Monitoramento",
        }}
        {...(!loading && {
          footer: {
            secondary: {
              children: tab === "consume" ? "Fechar" : "Continuar",
              onClick:
                tab === "consume"
                  ? onClose
                  : () => {
                      setTab("consume");
                    },
              disabled: tab === "default" ? !selectedGroups.length : false,
              icon: tab === "default" ? <ChevronRightIcon /> : undefined,
            },
            ...(tab === "consume" && {
              primary: {
                icon: hasBalanceToBuy ? (
                  <CircleStackIcon />
                ) : (
                  <ExternalLinkIcon />
                ),
                children: hasBalanceToBuy
                  ? "Sim, atualizar carteira"
                  : "Comprar créditos",
                onClick: onSubmit,
                loading: create.isLoading,
                disabled: fullUpdated,
              },
            }),
          },
        })}
      >
        <div className="grid gap-6">
          {loading && (
            <>
              <Skeleton height={180} />
              <Skeleton height={20} />
            </>
          )}

          {!loading && (
            <>
              <div className="grid gap-3">
                {tab === "default" && (
                  <>
                    <Alert>
                      <Label>
                        Defina os grupos de vencimento conforme sua preferência:
                      </Label>
                    </Alert>

                    <div className="grid grid-cols-2 gap-x-2 gap-y-3">
                      {MONITORING_GROUPS.map(({ value, label }) => {
                        if (selectedGroups.includes("all") && value !== "all")
                          return null;

                        const className =
                          value === "all" ? "col-span-2" : undefined;

                        return (
                          <Switch
                            key={`monitoring-refresh-group-${value}`}
                            id={value}
                            label={label}
                            layout="box"
                            className={className}
                            value={value}
                            checked={selectedGroups.includes(value)}
                            onChange={() => {
                              if (value === "all") {
                                setSelectedGroups((state) =>
                                  state.includes("all") ? [] : ["all"],
                                );
                                return;
                              }

                              setSelectedGroups((state) => {
                                return state.includes(value)
                                  ? state.filter(
                                      (stateValue) => stateValue !== value,
                                    )
                                  : [...state, value];
                              });
                            }}
                          />
                        );
                      })}
                    </div>
                  </>
                )}

                {tab === "consume" && (
                  <div className="grid gap-2 rounded border border-neutral-light-2 p-3">
                    <div className="flex flex-wrap items-center gap-1">
                      {MONITORING_GROUPS.map(({ value, label }) => {
                        if (
                          !selectedGroups.includes(value) ||
                          (selectedGroups.includes("all") && value !== "all")
                        )
                          return null;

                        return (
                          <TagFilter
                            key={`monitoring-refresh-select-item-${value}`}
                            size="sm"
                            label={label}
                          />
                        );
                      })}
                    </div>

                    <Link
                      kind="secondary"
                      size="sm"
                      icon={<EditIcon />}
                      onClick={() => {
                        setTab("default");
                      }}
                    >
                      Editar grupo
                    </Link>
                  </div>
                )}
              </div>

              {tab === "consume" && (
                <>
                  <div className="grid gap-4 rounded border border-neutral-light-2 p-4">
                    <CardPreview
                      icon={<UsersIcon />}
                      title={`${updateRequest.data?.update_clients_count} clientes para atualização`}
                      description={
                        <div className="flex items-center gap-1">
                          <p className="text-sm text-neutral-dark-1">
                            {updateRequest.data?.clients_count} clientes totais
                          </p>

                          <Tooltip content="Esse número é o total de clientes que você tem na carteira, independente de quantos clientes foram selecionados para atualização.">
                            <QuestionMarkIcon className="h-3 w-3 text-neutral-dark-1" />
                          </Tooltip>
                        </div>
                      }
                    />

                    {!!updateRequest.data?.update_clients_count && (
                      <div className="grid gap-2">
                        {[
                          {
                            label: "Crédito necessário",
                            kind: "highlight" as const,
                            value: updateRequest.data?.total_update_cost,
                          },
                          {
                            label: "Crédito atual",
                            kind: hasBalanceToBuy
                              ? ("success" as const)
                              : ("error" as const),
                            value: updateRequest.data?.current_balance,
                            text: !hasBalanceToBuy && "text-feedback-error",
                          },
                        ].map(({ label, kind, value, text }) => {
                          return (
                            <LabeledDivider
                              key={`refresh-dialog-summary-item-${label}`}
                            >
                              <span
                                className={`text-base ${text || "text-neutral-dark-1"}`}
                              >
                                {label}
                              </span>

                              <Tag
                                icon={<CircleStackIcon />}
                                kind={kind}
                                label={value}
                              />
                            </LabeledDivider>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {fullUpdated ? (
                    <p className="flex items-center justify-center gap-2 text-lg text-brand-green-dark">
                      <CheckCircleSolidIcon className="h-5 w-5" />
                      Monitoramento totalmente atualizado.
                    </p>
                  ) : (
                    <p className="text-lg text-brand-green-dark">
                      {hasBalanceToBuy
                        ? "Tem certeza que deseja atualizar Raio-X do grupo de vencimento selecionado?"
                        : "Você não possui créditos suficientes para essa transação."}
                    </p>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </Dialog>

      {buyOpen && <CreditDialog onClose={toggleBuy} />}
    </>
  );
}

export default React.memo(MonitoringRefreshDialog);
